import { useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box, Button, Stack } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import EditableList from '../EditableList/EditableList';
import CopagoArticleImportDialog from './CopagoArticleImportDialog';
import CopagoArticleMatchingDialog from './CopagoArticleMatchingDialog';
import CopagoPriceDiscrepancyDialog from './CopagoPriceDiscrepancyDialog';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function CopagoArticleManagement() {
  const authCtx = useContext(AuthContext);
  const [importOpen, setImportOpen] = useState(false);
  const [matchingOpen, setMatchingOpen] = useState(false);
  const [discrepanciesOpen, setDiscrepanciesOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  if (!authCtx.checkRight('copagoArticle:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const canImport = authCtx.checkRight('copagoArticle:import');

  const schema = {
    isMatched: {
      label: 'Matching',
      type: 'text',
      readOnly: true,
      excludeFromAdd: true,
      minWidth: '160px',
      // Only active, sellable articles are expected to be matched; everything
      // else (deposit articles, deactivated articles) shows a neutral dash.
      listDisplayValue: (value, item) => {
        if (item?.active === false || item?.sellable === false) return '—';
        return value ? '✓ gematcht' : '⚠️ Nicht gematcht';
      },
    },
    copagoId: { label: 'Copago Artikel ID', type: 'number', required: true, minWidth: '140px' },
    copagoSKU: { label: 'Copago SKU', type: 'number', required: true, minWidth: '140px' },
    name: { label: 'Name', type: 'text', required: true, minWidth: '240px' },
    active: { label: 'Aktiv', type: 'boolean', required: true },
    groupCopagoId: { label: 'Warengruppe ID (raw)', type: 'number', required: false },
    group: {
      label: 'Warengruppe',
      type: 'select',
      api: { resource: 'copagoGroup', labelField: 'name', valueField: '_id' },
      required: false,
      placeholder: 'Warengruppe wählen',
      listDisplayValue: value => value?.name || '—',
    },
    baseUnit: { label: 'Grundeinheit', type: 'number' },
    vatRatePercent: { label: 'MwSt %', type: 'number' },
    sellable: { label: 'Verkaufsfähig', type: 'boolean' },
    freePriceEntry: { label: 'Freie Preiseingabe', type: 'boolean' },
    depositArticleCopagoId: { label: 'Pfand Artikel Nr', type: 'number' },
    productInfo: { label: 'Produkt-Info', type: 'text', previewInList: true },
    customerInfo: { label: 'Kunden-Info', type: 'text', previewInList: true },
    otherInfo: { label: 'Sonstige Info', type: 'text', previewInList: true },
    printText: { label: 'Drucktext', type: 'text', previewInList: true },
    priceCents: { label: 'Preis (ct)', type: 'number' },
    priceOverrides: {
      label: 'Preis Overrides',
      type: 'nested',
      addButtonText: 'Branch-Preis hinzufügen',
      excludeFromAdd: true,
      api: { itemField: 'priceOverrides', nestedEndpoint: 'priceOverride' },
      schema: {
        branch: {
          label: 'Branch',
          type: 'select',
          api: { resource: 'branch', labelField: 'name', valueField: '_id' },
          required: true,
        },
        priceCents: { label: 'Preis (ct)', type: 'number', required: true },
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
      <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ mb: 1 }}>
        <Button variant='outlined' startIcon={<LinkIcon />} onClick={() => setMatchingOpen(true)}>
          Matching-Übersicht
        </Button>
        <Button
          variant='outlined'
          startIcon={<PriceChangeIcon />}
          onClick={() => setDiscrepanciesOpen(true)}
        >
          Preisdiskrepanzen
        </Button>
        {canImport ? (
          <Button
            variant='outlined'
            startIcon={<UploadFileIcon />}
            onClick={() => setImportOpen(true)}
          >
            CSV Import
          </Button>
        ) : null}
      </Stack>
      <EditableList
        key={reloadKey}
        schema={schema}
        title='Neuen Artikel anlegen'
        resourceType='copagoArticle'
        searchPlaceholder='Copago Artikel durchsuchen'
        parentIsEditable={authCtx.checkRight('copagoArticle:update')}
        options={{
          titleKey: 'name',
          listConfig: {
            endpoint: '/api/v1/copagoArticle',
            resultsKey: 'copagoArticles',
            pageParam: 'page',
            limitParam: 'limit',
          },
          serverSideFiltering: true,
          serverSideSorting: true,
        }}
      />
      {canImport ? (
        <CopagoArticleImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={() => setReloadKey(k => k + 1)}
        />
      ) : null}
      <CopagoArticleMatchingDialog open={matchingOpen} onClose={() => setMatchingOpen(false)} />
      <CopagoPriceDiscrepancyDialog
        open={discrepanciesOpen}
        onClose={() => setDiscrepanciesOpen(false)}
      />
    </Box>
  );
}

export default CopagoArticleManagement;
